import { Fragment, useCallback, useEffect, useState } from 'react'
import Layout from '../../components/Layout'
import {
  getSettledMarkets,
  getSettledBets,
  voidMarketAfterSettlement,
  voidBetAfterSettlement,
  deleteSportsBet
} from '../../services/api'
import SettlementVoidModal from '../../components/SettlementVoidModal'

/**
 * Void Bets (After Settlement) – jo markets settle ho chuke hain unko ya unki
 * kisi ek bet ko wapas void karna.
 *
 * Server sirf ek window (default 24h) ke andar settle hui cheezein hi void
 * karne deta hai — window ka number bhi wahi bhejta hai, UI usko dikhata hai.
 * Dono void endpoints transaction password maangte hain kyunki ye user ke
 * paise ulta karte hain.
 *
 * DELETE BET isi page ka teesra action hai. Paisa void jitna hi wapas jaata hai,
 * farq sirf trail ka hai: void statement me ek ulti '-void' line chhodta hai,
 * delete bet ki row aur uske saare statement nishaan hi mita deta hai. Wapas
 * nahi aata, isliye alag (dark) button aur alag warning.
 */
const LIMIT = 200

const MARKETS_FOR_FANCY = [
  '1st Innings 6 Overs Line', '2nd Innings 6 Overs Line', '3rd Innings 6 Overs Line',
  '1st Innings 50 Overs Line', '2nd Innings 50 Overs Line', '3rd Innings 50 Overs Line',
  '1st Innings 40 Overs Line', '2nd Innings 40 Overs Line', '3rd Innings 40 Overs Line',
  '1st Innings 30 Overs Line', '2nd Innings 30 Overs Line', '3rd Innings 30 Overs Line',
  '1st Innings 20 Overs Line', '2nd Innings 20 Overs Line', '3rd Innings 20 Overs Line',
  '1st Innings 10 Overs Line', '2nd Innings 10 Overs Line', '3rd Innings 10 Overs Line',
  'Over By Over', 'Ball By Ball', 'Normal', 'khado', 'meter', 'fancy1', 'oddeven'
]

const TABS = [
  { key: 'MATCH_ODDS', label: 'Match Odds' },
  { key: 'BOOKMAKER', label: 'Bookmaker' },
  { key: 'FANCY', label: 'Fancy' },
  { key: 'OTHERS', label: 'Others' }
]

// Server market_type ko waise hi bhejta hai jaise bet me tha — 'MATCH_ODDS',
// 'Bookmaker', 'oddeven'… — isliye compare case-insensitive hona chahiye,
// warna 'Bookmaker' galti se OTHERS me chala jaata hai.
const FANCY_UPPER = MARKETS_FOR_FANCY.map((m) => m.toUpperCase())

function getTabForMarket(mkt) {
  const mt = String(mkt.marketType || '').toUpperCase()
  if (mt === 'MATCH_ODDS') return 'MATCH_ODDS'
  if (mt === 'BOOKMAKER') return 'BOOKMAKER'
  if (FANCY_UPPER.includes(mt)) return 'FANCY'
  return 'OTHERS'
}

const netClass = (v) => (v >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold')

export default function VoidBets() {
  const [activeTab, setActiveTab] = useState('MATCH_ODDS')
  const [markets, setMarkets] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')

  const [expandedMarket, setExpandedMarket] = useState(null)
  const [bets, setBets] = useState([])
  const [betsLoading, setBetsLoading] = useState(false)

  const [confirmModal, setConfirmModal] = useState(null)
  const [txnPassword, setTxnPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState(null)
  const [windowHours, setWindowHours] = useState(24)

  const fetchMarkets = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await getSettledMarkets({ limit: LIMIT, offset: 0, search: query })
      if (resp?.windowHours) setWindowHours(Number(resp.windowHours))
      setMarkets(resp?.success && Array.isArray(resp.data) ? resp.data : [])
    } catch {
      setMarkets([])
    } finally {
      setLoading(false)
    }
  }, [query])

  useEffect(() => { fetchMarkets() }, [fetchMarkets])

  const filteredMarkets = markets.filter((mkt) => getTabForMarket(mkt) === activeTab)

  const tabCounts = {}
  TABS.forEach((t) => { tabCounts[t.key] = 0 })
  markets.forEach((mkt) => { tabCounts[getTabForMarket(mkt)] += 1 })

  const fetchBets = async (matchId, marketType) => {
    setBetsLoading(true)
    try {
      const resp = await getSettledBets({ match_id: matchId, market_type: marketType })
      setBets(resp?.success && Array.isArray(resp.data) ? resp.data : [])
    } catch {
      setBets([])
    } finally {
      setBetsLoading(false)
    }
  }

  const isExpanded = (mkt) =>
    expandedMarket?.matchId === mkt.matchId && expandedMarket?.marketType === mkt.marketType

  const handleExpandMarket = (market) => {
    if (isExpanded(market)) {
      setExpandedMarket(null)
      setBets([])
    } else {
      setExpandedMarket({ matchId: market.matchId, marketType: market.marketType })
      fetchBets(market.matchId, market.marketType)
    }
  }

  const openConfirm = (type, data) => {
    setTxnPassword('')
    setError('')
    setNotice(null)
    setConfirmModal({ type, data })
  }

  const confirmVoid = async () => {
    if (!confirmModal) return
    setSubmitting(true)
    setError('')
    try {
      let resp
      if (confirmModal.type === 'market') {
        resp = await voidMarketAfterSettlement({
          match_id: confirmModal.data.matchId,
          market_type: confirmModal.data.marketType,
          transactionPassword: txnPassword
        })
      } else if (confirmModal.type === 'delete') {
        // ledger_id bhejte hain (bet_id nahi): MO/BM me ek settlement row poore
        // market book ki hoti hai, server usi se pura group nikalta hai.
        resp = await deleteSportsBet({
          ledger_id: confirmModal.data.ledgerId,
          transactionPassword: txnPassword
        })
      } else {
        resp = await voidBetAfterSettlement({
          ledger_id: confirmModal.data.ledgerId,
          transactionPassword: txnPassword
        })
      }

      if (resp?.success) {
        const who = confirmModal.data.username || confirmModal.data.userId
        setNotice({
          ok: true,
          message: confirmModal.type === 'market'
            ? `Market voided successfully. Affected users: ${resp.affectedUsers || 0}`
            : confirmModal.type === 'delete'
              ? `${resp.betsDeleted || 0} bet(s) deleted for ${who} — `
                + `${resp.statementRowsRemoved || 0} statement row(s) removed`
              : `Bet voided successfully for user: ${who}`
        })
        setConfirmModal(null)
        fetchMarkets()
        if (expandedMarket) fetchBets(expandedMarket.matchId, expandedMarket.marketType)
      } else {
        setError(resp?.error || 'Unknown error')
      }
    } catch (err) {
      setError(
        err?.response?.data?.error || err?.response?.data?.message || err.message
      )
    } finally {
      setSubmitting(false)
    }
  }

  const betTypeBadge = (bt) => {
    if (!bt) return <span className="text-muted">-</span>
    const isBack = bt === 'back' || bt === 'yes' || bt === 'Yes'
    return <span className={`badge ${isBack ? 'bg-primary' : 'bg-danger'} px-2`}>{bt.toUpperCase()}</span>
  }

  const resultBadge = (status) => {
    if (!status) return <span className="text-muted">-</span>
    const cls = status === 'won' ? 'bg-success' : status === 'loss' ? 'bg-danger' : 'bg-secondary'
    return <span className={`badge ${cls} px-2`}>{status.toUpperCase()}</span>
  }

  const runSearch = () => { setQuery(search) }

  return (
    <Layout title="Void Bets - Diamond Admin">
      <div className="listing-grid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-flex align-items-center justify-content-between">
              <h4 className="mb-0 font-size-18">
                Void Bets (After Settlement) — last {windowHours}h
              </h4>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={fetchMarkets}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {notice && (
          <div className="row">
            <div className="col-12">
              <div className={`alert py-2 ${notice.ok ? 'alert-success' : 'alert-danger'}`}>
                {notice.message}
              </div>
            </div>
          </div>
        )}

        <div className="row mb-3">
          <div className="col-md-8">
            <div className="input-group input-group-sm">
              <input
                type="text"
                className="form-control"
                placeholder="Search by match ID, market type, team name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runSearch() }}
              />
              <button type="button" className="btn btn-primary" onClick={runSearch}>
                Search
              </button>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <ul className="nav nav-tabs mb-3">
              {TABS.map((tab) => (
                <li className="nav-item" key={tab.key}>
                  <button
                    type="button"
                    className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={() => { setActiveTab(tab.key); setExpandedMarket(null); setBets([]) }}
                  >
                    {tab.label} ({tabCounts[tab.key] || 0})
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="text-start">Match Title</th>
                        <th className="text-start">Market Type</th>
                        <th className="text-end">Entries</th>
                        <th className="text-end">Users</th>
                        <th className="text-end">Net Amount</th>
                        <th className="text-center" style={{ minWidth: '220px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={6} className="text-center py-4">Loading...</td></tr>
                      ) : filteredMarkets.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-4">
                            No markets settled in the last {windowHours} hours
                          </td>
                        </tr>
                      ) : (
                        filteredMarkets.map((mkt, idx) => (
                          <Fragment key={`${mkt.matchId}-${mkt.marketType}-${idx}`}>
                            <tr>
                              <td>
                                <div className="fw-bold text-truncate" style={{ maxWidth: '240px' }}>
                                  {mkt.matchTitle
                                    || (mkt.teamOne && mkt.teamTwo ? `${mkt.teamOne} vs ${mkt.teamTwo}` : mkt.matchId)}
                                </div>
                                <small className="text-muted">{mkt.matchId}</small>
                              </td>
                              <td className="text-truncate" style={{ maxWidth: '180px' }}>
                                {mkt.marketType || '-'}
                              </td>
                              <td className="text-end">{mkt.totalEntries}</td>
                              <td className="text-end">{mkt.totalUsers}</td>
                              <td className="text-end">
                                <span className={netClass(mkt.totalNetAmount)}>
                                  {mkt.totalNetAmount >= 0 ? '+' : ''}
                                  {Number(mkt.totalNetAmount || 0).toFixed(2)}
                                </span>
                              </td>
                              <td className="text-center">
                                <div className="btn-group btn-group-sm">
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => handleExpandMarket(mkt)}
                                  >
                                    {isExpanded(mkt) ? 'Hide Bets' : 'View Bets'}
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => openConfirm('market', mkt)}
                                  >
                                    Void Market
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {isExpanded(mkt) && (
                              <tr>
                                <td colSpan={6} className="bg-light p-3">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong>Individual Bets — {mkt.marketType}</strong>
                                    <small className="text-muted">
                                      {bets.length} bet{bets.length !== 1 ? 's' : ''}
                                    </small>
                                  </div>
                                  {betsLoading ? (
                                    <div className="text-center text-muted py-3">Loading bets...</div>
                                  ) : bets.length === 0 ? (
                                    <div className="text-center text-muted py-3">
                                      No bets found for this market
                                    </div>
                                  ) : (
                                    <div className="table-responsive">
                                      <table className="table table-sm table-bordered mb-0">
                                        <thead className="table-light">
                                          <tr>
                                            <th>Bet ID</th>
                                            <th>User</th>
                                            <th>Selection</th>
                                            <th className="text-center">Bet Type</th>
                                            <th className="text-end">Odds</th>
                                            <th className="text-end">Stake</th>
                                            <th className="text-end">Net Amount</th>
                                            <th className="text-center">Result</th>
                                            <th className="text-center">Action</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {bets.map((bet) => (
                                            <tr key={bet.ledgerId}>
                                              <td>#{bet.betId}</td>
                                              <td>{bet.username}</td>
                                              <td>{bet.selectionName || '-'}</td>
                                              <td className="text-center">{betTypeBadge(bet.betType)}</td>
                                              <td className="text-end">{bet.odds ?? '-'}</td>
                                              <td className="text-end">
                                                {bet.stakeAmount != null ? Number(bet.stakeAmount).toFixed(2) : '-'}
                                              </td>
                                              <td className="text-end">
                                                <span className={netClass(bet.netamount)}>
                                                  {bet.netamount >= 0 ? '+' : ''}
                                                  {Number(bet.netamount || 0).toFixed(2)}
                                                </span>
                                              </td>
                                              <td className="text-center">{resultBadge(bet.resultStatus)}</td>
                                              <td className="text-center">
                                                <div className="btn-group btn-group-sm">
                                                  <button
                                                    type="button"
                                                    className="btn btn-warning"
                                                    onClick={() => openConfirm('bet', bet)}
                                                  >
                                                    Void Bet
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="btn btn-dark"
                                                    onClick={() => openConfirm('delete', bet)}
                                                  >
                                                    Delete Bet
                                                  </button>
                                                </div>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SettlementVoidModal
        show={!!confirmModal}
        title={
          confirmModal?.type === 'market'
            ? 'Void Entire Market'
            : confirmModal?.type === 'delete'
              ? 'Delete Bet — permanent'
              : 'Void Single Bet'
        }
        onHide={() => setConfirmModal(null)}
        onConfirm={confirmVoid}
        submitting={submitting}
        confirmLabel={confirmModal?.type === 'delete' ? 'Delete Permanently' : 'Confirm Void'}
        requirePassword
        password={txnPassword}
        onPasswordChange={setTxnPassword}
        error={error}
      >
        {confirmModal?.type === 'market' ? (
          <>
            <p className="mb-2">
              Void <strong>all bets</strong> in market <strong>{confirmModal.data.marketType}</strong>{' '}
              for match <strong>{confirmModal.data.matchTitle || confirmModal.data.matchId}</strong>?
            </p>
            <p className="mb-0 text-muted">
              This will reverse settled amounts for <strong>{confirmModal.data.totalEntries}</strong>{' '}
              entries across <strong>{confirmModal.data.totalUsers}</strong> users. This action cannot
              be undone.
            </p>
          </>
        ) : confirmModal?.type === 'delete' ? (
          <>
            <p className="mb-2">
              Delete bet <strong>#{confirmModal.data.betId}</strong> for user{' '}
              <strong>{confirmModal.data.username}</strong>?
            </p>
            <p className="mb-2">
              The settlement is reversed exactly like a void —{' '}
              <span className={netClass(confirmModal.data.netamount)}>
                {confirmModal.data.netamount >= 0 ? '+' : ''}
                {Number(confirmModal.data.netamount || 0).toFixed(2)}
              </span>{' '}
              goes back — and then <strong>every trace of the bet is removed</strong>: the bet
              itself, its settlement and commission entries, and its point entries. Nothing will
              be left in the user&apos;s account statement.
            </p>
            <div className="alert alert-danger mb-0 py-2">
              <strong>This cannot be undone.</strong> On Match Odds and Bookmaker one settlement
              covers the user&apos;s whole book for that market, so every bet of theirs in it is
              deleted together. A copy is kept in the void audit and activity log only.
            </div>
          </>
        ) : confirmModal ? (
          <>
            <p className="mb-2">
              Void bet <strong>#{confirmModal.data.betId}</strong> for user{' '}
              <strong>{confirmModal.data.username}</strong>?
            </p>
            <p className="mb-0 text-muted">
              Net amount to reverse:{' '}
              <span className={netClass(confirmModal.data.netamount)}>
                {confirmModal.data.netamount >= 0 ? '+' : ''}
                {Number(confirmModal.data.netamount || 0).toFixed(2)}
              </span>
              . This action cannot be undone.
            </p>
          </>
        ) : null}
      </SettlementVoidModal>
    </Layout>
  )
}
